import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import trackingLinkService from '../services/trackingLinkService';
import campaignService from '../services/campaignService';
import LoadingSpinner from '../components/LoadingSpinner';

const TrackingRedirect = () => {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaign, setCampaign] = useState(null);

  useEffect(() => {
    const handleTracking = async () => {
      try {
        setLoading(true);
        
        // Get tracking link data using the tracking code
        const trackingLink = await trackingLinkService.getTrackingLinkByCode(shortCode);

        if (!trackingLink) {
          setError('Tracking link not found or expired');
          return;
        }

        // Get campaign details
        const campaignData = await campaignService.getCampaignById(trackingLink.campaignId);
        if (!campaignData) {
          setError('Campaign not found');
          return;
        }

        setCampaign(campaignData);

        // Track the click with additional data
        const clickData = {
          userAgent: navigator.userAgent,
          referrer: document.referrer,
          timestamp: new Date().toISOString(),
          ip: null, // Will be handled server-side if needed
          platform: navigator.platform,
          language: navigator.language
        };

        await trackingLinkService.trackClick(trackingLink.id, clickData);

        // Redirect to campaign page after a short delay
        setTimeout(() => {
          // Redirect to the public campaign page or brand website
          if (campaignData.website) {
            window.location.href = campaignData.website;
          } else if (campaignData.brandWebsite) {
            window.location.href = campaignData.brandWebsite;
          } else {
            // Redirect to campaign details page
            navigate(`/campaigns/${campaignData.id}`);
          }
        }, 2000);

      } catch (error) {
        console.error('Error handling tracking:', error);
        setError('An error occurred while processing the tracking link');
      } finally {
        setLoading(false);
      }
    };

    if (shortCode) {
      handleTracking();
    } else {
      setError('Invalid tracking link');
      setLoading(false);
    }
  }, [shortCode, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <LoadingSpinner size="lg" color="primary" />
              <h2 className="mt-4 text-lg font-medium text-gray-900">
                Processing your request...
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Please wait while we redirect you to the campaign.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="mt-4 text-lg font-medium text-gray-900">
                Link Not Found
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {error}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Go to Homepage
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state - show campaign info while redirecting
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-4 text-lg font-medium text-gray-900">
              Redirecting to Campaign
            </h2>
            {campaign && (
              <div className="mt-4">
                <h3 className="text-md font-medium text-gray-800">{campaign.name || campaign.title}</h3>
                <p className="mt-1 text-sm text-gray-600">{campaign.description}</p>
              </div>
            )}
            <p className="mt-4 text-sm text-gray-600">
              You will be redirected automatically in a few seconds...
            </p>
            <div className="mt-4">
              <LoadingSpinner size="sm" color="primary" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingRedirect;
